import type { EditorView } from "@codemirror/view";
import {
  Bold,
  BookOpen,
  Calculator,
  ChevronDown,
  Ellipsis,
  Eye,
  FilePen,
  Image,
  ImagePlus,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link,
  List,
  ListOrdered,
  MessageSquarePlus,
  Pencil,
  Redo2,
  Search,
  Table,
  Tag,
  Undo2,
  Upload,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "../lib/app";
import { openFind } from "../editor/find";
import { insertImageFiles } from "../editor/images";
import {
  canRedo,
  canUndo,
  headingAt,
  HEADINGS,
  indentList,
  insertBulletList,
  insertCitation,
  insertCrossRef,
  insertDisplayMath,
  insertFigure,
  insertInlineMath,
  insertLink,
  insertNumberedList,
  insertSymbol,
  insertTable,
  isBold,
  isItalic,
  outdentList,
  redo,
  selectWordIfEmpty,
  setHeading,
  toggleBold,
  toggleItalic,
  undo,
  type HeadingLevel,
} from "../editor/toolbar";
import { Popover } from "./Popover";
import { SymbolPicker } from "./SymbolPicker";

const MAC = /Mac|iPhone|iPad/.test(navigator.platform);
const key = (k: string) => (MAC ? k.replace("Mod-", "⌘").replace("Shift-", "⇧").replace("Alt-", "⌥") : k.replace("Mod-", "Ctrl+").replace("Shift-", "Shift+").replace("Alt-", "Alt+"));
const FIGURE_FILE = /\.(png|jpe?g|pdf|eps|svg)$/i;

/** Groups in toolbar order. When space runs out, the lowest priority goes into the ⋯ menu first. */
const GROUPS = ["history", "heading", "style", "math", "insert", "list"] as const;
type Group = (typeof GROUPS)[number];
const PRIORITY: Record<Group, number> = { history: 6, style: 5, math: 4, insert: 3, heading: 2, list: 1 };

export type EditorMode = "code" | "visual";

interface Snapshot {
  bold: boolean;
  italic: boolean;
  heading: HeadingLevel;
  undo: boolean;
  redo: boolean;
}

function snapshot(view: EditorView | null): Snapshot | null {
  if (!view) return null;
  const s = view.state;
  return { bold: isBold(s), italic: isItalic(s), heading: headingAt(s), undo: canUndo(s), redo: canRedo(s) };
}

/** Formatting and insert buttons above the editor, plus the code/visual switch and editing/reviewing mode. */
export function EditorToolbar({
  getView,
  subscribe,
  canEdit,
  tracking,
  trackAll,
  onTracking,
  mode,
  visualAvailable,
  onMode,
  onComment,
  figureFiles,
  start,
}: {
  getView: () => EditorView | null;
  /** Calls back after every editor update (typing, cursor moves, switching files). */
  subscribe: (fn: () => void) => () => void;
  canEdit: boolean;
  tracking: boolean;
  /** Track changes is on for everyone in project settings. */
  trackAll: boolean;
  onTracking: (on: boolean) => void;
  mode: EditorMode;
  /** Visual editing only applies to .tex files. */
  visualAvailable: boolean;
  onMode: (mode: EditorMode) => void;
  onComment: (from: number, to: number) => void;
  /** Images already in the project, for Insert figure → From project files. */
  figureFiles: () => string[];
  /** Extra buttons at the very start. */
  start?: ReactNode;
}) {
  const [snap, setSnap] = useState<Snapshot | null>(() => snapshot(getView()));
  const [open, setOpen] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const [hidden, setHidden] = useState<Group[]>([]);
  const bar = useRef<HTMLDivElement>(null);
  const right = useRef<HTMLDivElement>(null);
  const widths = useRef<Partial<Record<Group, number>>>({});
  const upload = useRef<HTMLInputElement>(null);
  const more = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame = 0;
    const update = () =>
      setSnap((prev) => {
        const next = snapshot(getView());
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
    update();
    const off = subscribe(() => {
      update();
      // The undo manager files an undo onto the redo stack after the editor has updated; look again.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    });
    return () => {
      off();
      cancelAnimationFrame(frame);
    };
  }, [getView, subscribe]);

  // Move groups into the ⋯ menu when the editor column is too narrow for them all.
  useLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;
    const fit = () => {
      for (const g of el.querySelectorAll<HTMLElement>(":scope > .tb-left > [data-group]")) widths.current[g.dataset.group as Group] = g.offsetWidth + 6;
      const room = el.clientWidth - (right.current?.offsetWidth ?? 0) - 48;
      let total = GROUPS.reduce((n, g) => n + (widths.current[g] ?? 0), 0);
      const hide: Group[] = [];
      for (const g of [...GROUPS].sort((a, b) => PRIORITY[a] - PRIORITY[b])) {
        if (total <= room) break;
        hide.push(g);
        total -= widths.current[g] ?? 0;
      }
      setHidden((h) => (h.join() === hide.join() ? h : hide));
    };
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    if (right.current) ro.observe(right.current);
    fit();
    return () => ro.disconnect();
  }, []);

  const run = (cmd: (view: EditorView) => boolean) => {
    const view = getView();
    if (view) cmd(view);
  };
  const toggle = (id: string) => (e: React.MouseEvent<HTMLElement>) => {
    // Menus opened from the ⋯ panel hang from the ⋯ button, since the panel closes.
    const fromMore = !!e.currentTarget.closest(".tb-more");
    const anchor = fromMore ? more.current?.querySelector("button") ?? e.currentTarget : e.currentTarget;
    setOpen((o) => (o?.id === id ? null : { id, anchor }));
  };
  const close = () => setOpen(null);
  const ro = !canEdit;
  const headingLabel = HEADINGS.find((h) => h.level === snap?.heading)?.label ?? "Normal text";

  const groups: Record<Group, ReactNode> = {
    history: (
      <>
        <Btn label="Undo" hint={key("Mod-z")} disabled={ro || !snap?.undo} onClick={() => run(undo)}>
          <Undo2 size={16} />
        </Btn>
        <Btn label="Redo" hint={key(MAC ? "Mod-Shift-z" : "Mod-y")} disabled={ro || !snap?.redo} onClick={() => run(redo)}>
          <Redo2 size={16} />
        </Btn>
      </>
    ),
    heading: (
      <button
        type="button"
        className={`tb-btn tb-heading${open?.id === "heading" ? " on" : ""}`}
        title="Section heading level"
        aria-haspopup="menu"
        aria-expanded={open?.id === "heading"}
        disabled={ro}
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle("heading")}
      >
        <span>{headingLabel}</span>
        <ChevronDown size={13} />
      </button>
    ),
    style: (
      <>
        <Btn label="Bold" hint={key("Mod-b")} active={snap?.bold} disabled={ro} onClick={() => run(toggleBold)}>
          <Bold size={16} />
        </Btn>
        <Btn label="Italic" hint={key("Mod-i")} active={snap?.italic} disabled={ro} onClick={() => run(toggleItalic)}>
          <Italic size={16} />
        </Btn>
      </>
    ),
    math: (
      <>
        <Btn label="Insert math" menu open={open?.id === "math"} disabled={ro} onClick={toggle("math")}>
          <Calculator size={16} />
        </Btn>
        <Btn label="Insert symbol" open={open?.id === "symbol"} disabled={ro} onClick={toggle("symbol")}>
          <span className="tb-omega">Ω</span>
        </Btn>
      </>
    ),
    insert: (
      <>
        <Btn label="Insert link" disabled={ro} onClick={() => run(insertLink)}>
          <Link size={16} />
        </Btn>
        <Btn
          label="Add comment"
          hint={key("Mod-Alt-m")}
          disabled={ro}
          onClick={() => {
            const view = getView();
            const sel = view && selectWordIfEmpty(view);
            if (sel) onComment(sel.from, sel.to);
            else toast("Select some text to comment on");
          }}
        >
          <MessageSquarePlus size={16} />
        </Btn>
        <Btn label="Insert cross reference" disabled={ro} onClick={() => run(insertCrossRef)}>
          <Tag size={16} />
        </Btn>
        <Btn label="Insert citation" disabled={ro} onClick={() => run(insertCitation)}>
          <BookOpen size={16} />
        </Btn>
        <Btn label="Insert figure" menu open={open?.id === "figure"} disabled={ro} onClick={toggle("figure")}>
          <ImagePlus size={16} />
        </Btn>
        <Btn label="Insert table" menu open={open?.id === "table"} disabled={ro} onClick={toggle("table")}>
          <Table size={16} />
        </Btn>
      </>
    ),
    list: (
      <Btn label="Insert list" menu open={open?.id === "list"} disabled={ro} onClick={toggle("list")}>
        <List size={16} />
      </Btn>
    ),
  };

  const visible = GROUPS.filter((g) => !hidden.includes(g));
  const reviewLabel = !canEdit ? "Viewing" : tracking ? "Reviewing" : "Editing";

  return (
    <div className="tb" role="toolbar" aria-label="Editor tools" ref={bar}>
      <div className="tb-left">
        {start}
        {visible.map((g) => (
          <div key={g} className="tb-group" data-group={g}>
            {groups[g]}
          </div>
        ))}
        {hidden.length > 0 && (
          <div ref={more} className="tb-more-btn">
            <Btn label="More editor tools" open={open?.id === "more"} onClick={toggle("more")}>
              <Ellipsis size={16} />
            </Btn>
          </div>
        )}
      </div>
      <div className="tb-right" ref={right}>
        <div className="tb-switch" role="radiogroup" aria-label="Editor mode" title={visualAvailable ? undefined : "Visual editing works on .tex files"}>
          {(["code", "visual"] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              className={mode === m ? "on" : ""}
              disabled={m === "visual" && !visualAvailable}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onMode(m)}
            >
              {m === "code" ? "Code" : "Visual"}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`tb-btn tb-review${tracking ? " reviewing" : ""}${open?.id === "review" ? " on" : ""}`}
          title={!canEdit ? "You can view this project but not edit it" : "Editing or reviewing"}
          aria-haspopup="menu"
          disabled={!canEdit}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggle("review")}
        >
          {!canEdit ? <Eye size={15} /> : tracking ? <FilePen size={15} /> : <Pencil size={15} />}
          <span className="tb-review-label">{reviewLabel}</span>
          {canEdit && <ChevronDown size={13} />}
        </button>
        <Btn label="Search file" hint={key("Mod-f")} onClick={() => run((v) => openFind(v))}>
          <Search size={16} />
        </Btn>
      </div>

      <input
        ref={upload}
        type="file"
        accept="image/*,.pdf,.eps"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          if (files.length) run((v) => insertImageFiles(v, files));
        }}
      />

      {open?.id === "more" && (
        <Popover anchor={open.anchor} onClose={close} className="tb-more" label="More editor tools" align="end">
          {GROUPS.filter((g) => hidden.includes(g)).map((g) => (
            <div key={g} className="tb-group">
              {groups[g]}
            </div>
          ))}
        </Popover>
      )}
      {open?.id === "heading" && (
        <Popover anchor={open.anchor} onClose={close} className="menu-pop" label="Section heading level">
          {HEADINGS.map((h) => (
            <button
              key={h.level}
              className={`menu-item heading-${h.level}${snap?.heading === h.level ? " on" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                close();
                run((v) => setHeading(v, h.level));
              }}
            >
              {h.label}
            </button>
          ))}
        </Popover>
      )}
      {open?.id === "math" && (
        <Popover anchor={open.anchor} onClose={close} className="menu-pop" label="Insert math">
          <MenuItem onClick={() => (close(), run(insertInlineMath))} hint="\( \)">
            Inline math
          </MenuItem>
          <MenuItem onClick={() => (close(), run(insertDisplayMath))} hint="\[ \]">
            Display math
          </MenuItem>
        </Popover>
      )}
      {open?.id === "symbol" && <SymbolPicker anchor={open.anchor} onClose={close} onPick={(latex, math) => run((v) => insertSymbol(v, latex, math))} />}
      {open?.id === "figure" && (
        <FigureMenu
          anchor={open.anchor}
          files={figureFiles().filter((f) => FIGURE_FILE.test(f))}
          onClose={close}
          onUpload={() => {
            close();
            upload.current?.click();
          }}
          onPick={(path) => {
            close();
            run((v) => insertFigure(v, path));
          }}
          onEmpty={() => {
            close();
            run((v) => insertFigure(v));
          }}
        />
      )}
      {open?.id === "table" && (
        <TableGrid
          anchor={open.anchor}
          onClose={close}
          onPick={(rows, cols) => {
            close();
            run((v) => insertTable(v, rows, cols));
          }}
        />
      )}
      {open?.id === "list" && (
        <Popover anchor={open.anchor} onClose={close} className="menu-pop" label="Insert list">
          <MenuItem icon={<List size={15} />} onClick={() => (close(), run(insertBulletList))}>
            Bulleted list
          </MenuItem>
          <MenuItem icon={<ListOrdered size={15} />} onClick={() => (close(), run(insertNumberedList))}>
            Numbered list
          </MenuItem>
          <div className="menu-sep" />
          <MenuItem icon={<IndentIncrease size={15} />} hint="Tab" onClick={() => (close(), run(indentList))}>
            Increase indent
          </MenuItem>
          <MenuItem icon={<IndentDecrease size={15} />} hint="⇧Tab" onClick={() => (close(), run(outdentList))}>
            Decrease indent
          </MenuItem>
        </Popover>
      )}
      {open?.id === "review" && (
        <Popover anchor={open.anchor} onClose={close} className="menu-pop review-pop" label="Editing or reviewing" align="end">
          <MenuItem
            icon={<Pencil size={15} />}
            on={!tracking}
            disabled={trackAll}
            title={trackAll ? "Track changes is on for everyone in project settings" : undefined}
            onClick={() => (close(), onTracking(false))}
          >
            Editing
            <small>{trackAll ? "Turned off for everyone in project settings" : "Edit content directly"}</small>
          </MenuItem>
          <MenuItem icon={<FilePen size={15} />} on={tracking} onClick={() => (close(), onTracking(true))}>
            Reviewing
            <small>Edits become suggestions</small>
          </MenuItem>
        </Popover>
      )}
    </div>
  );
}

function Btn({
  label,
  hint,
  active,
  open,
  menu,
  disabled,
  onClick,
  children,
}: {
  label: string;
  hint?: string;
  active?: boolean;
  /** Its menu is showing. */
  open?: boolean;
  /** Opens a menu rather than acting straight away. */
  menu?: boolean;
  disabled?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`tb-btn${active ? " active" : ""}${open ? " on" : ""}`}
      title={hint ? `${label} (${hint})` : label}
      aria-label={label}
      aria-pressed={active}
      aria-haspopup={menu ? "menu" : undefined}
      aria-expanded={menu ? !!open : undefined}
      disabled={disabled}
      // Keep the editor's focus and selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function MenuItem({
  icon,
  hint,
  on,
  disabled,
  title,
  onClick,
  children,
}: {
  icon?: ReactNode;
  hint?: string;
  on?: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button className={`menu-item${on ? " on" : ""}`} role="menuitem" disabled={disabled} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {icon && <span className="menu-icon">{icon}</span>}
      <span className="menu-label">{children}</span>
      {hint && <kbd>{hint}</kbd>}
    </button>
  );
}

function FigureMenu({
  anchor,
  files,
  onClose,
  onUpload,
  onPick,
  onEmpty,
}: {
  anchor: HTMLElement;
  files: string[];
  onClose: () => void;
  onUpload: () => void;
  onPick: (path: string) => void;
  onEmpty: () => void;
}) {
  const [browsing, setBrowsing] = useState(false);
  const [query, setQuery] = useState("");
  const shown = files.filter((f) => f.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <Popover anchor={anchor} onClose={onClose} className="menu-pop figure-pop" label="Insert figure">
      {!browsing ? (
        <>
          <MenuItem icon={<Upload size={15} />} onClick={onUpload}>
            Upload from computer
          </MenuItem>
          <MenuItem icon={<Image size={15} />} disabled={!files.length} title={files.length ? undefined : "No images in this project yet"} onClick={() => setBrowsing(true)}>
            From project files
          </MenuItem>
          <MenuItem icon={<ImagePlus size={15} />} onClick={onEmpty}>
            Empty figure
          </MenuItem>
          <div className="menu-note">You can also paste or drop images straight into the editor.</div>
        </>
      ) : (
        <>
          <input className="menu-filter" autoFocus placeholder="Find an image" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && shown[0] && onPick(shown[0])} />
          <div className="figure-list">
            {shown.map((f) => (
              <button key={f} className="menu-item" title={f} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(f)}>
                <span className="menu-icon">
                  <Image size={14} />
                </span>
                <span className="menu-label mono">{f}</span>
              </button>
            ))}
            {!shown.length && <div className="menu-note">No images match</div>}
          </div>
        </>
      )}
    </Popover>
  );
}

const GRID_COLS = 10;
const GRID_ROWS = 8;

function TableGrid({ anchor, onClose, onPick }: { anchor: HTMLElement; onClose: () => void; onPick: (rows: number, cols: number) => void }) {
  const [size, setSize] = useState({ rows: 0, cols: 0 });
  return (
    <Popover anchor={anchor} onClose={onClose} className="table-pop" label="Insert table">
      <div className="table-grid" onMouseLeave={() => setSize({ rows: 0, cols: 0 })} style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}>
        {Array.from({ length: GRID_ROWS * GRID_COLS }, (_, i) => {
          const r = Math.floor(i / GRID_COLS) + 1;
          const c = (i % GRID_COLS) + 1;
          return (
            <button
              key={i}
              aria-label={`${r} by ${c} table`}
              className={r <= size.rows && c <= size.cols ? "on" : ""}
              onMouseEnter={() => setSize({ rows: r, cols: c })}
              onFocus={() => setSize({ rows: r, cols: c })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(r, c)}
            />
          );
        })}
      </div>
      <div className="table-size">{size.rows ? `${size.rows} × ${size.cols}` : "Choose a size"}</div>
    </Popover>
  );
}
